import { plateFont, numFont } from '../../utils/fonts';
import React, { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import useVehicleLookup from '../../hooks/useVehicleLookup';
import HelpButton from '../shared/HelpButton';
import ExtraLookupConfirmDialog from './ExtraLookupConfirmDialog';

export const EMPTY_VEHICLE = {
  registration_number: '',
  manufacturer: '',
  model: '',
  year_model: '',
  vin: '',
};

/**
 * Registration number field with the work-order dialog's lookup flow:
 * typeahead over the org's own vehicles, an explicit paid lookup, and a manual
 * entry panel that is always reachable — from the link under the field, from
 * the "not found" alert, and while a lookup is still running.
 *
 * The caller owns the vehicle object and decides what to do with it:
 *   onChange(nextVehicle)      the whole vehicle object, ready to store
 *   onExistingVehicle(vehicle) the reg resolved to a vehicle the org already
 *                              has (it carries an `id`), or null when the user
 *                              edits the reg again
 *   onLastCustomer(customer)   that vehicle's most recent customer
 *
 * Extracted from AddMaintenanceDialog's vehicle block so other forms get the
 * same flow. AddMaintenanceDialog still has its own copy (it is wired into OEM
 * packages, odometer warnings and CABAS import); this component deliberately
 * carries only the parts that stand alone.
 */
function VehicleRegistrationField({
  vehicle,
  onChange,
  onExistingVehicle,
  onLastCustomer,
  disabled = false,
  required = false,
  autoFocus = false,
  dataTestId = 'vehicle-registration',
}) {
  const { t } = useTranslation();
  const [manualEntry, setManualEntry] = useState(false);

  const vehicleLookup = useVehicleLookup({
    t,
    onVehicleData: (vehicleData, regNumber) => {
      onChange({
        ...vehicle,
        ...vehicleData,
        registration_number: (regNumber || '').toUpperCase(),
      });
      // A hit from the org's own database is an existing vehicle, not a new one
      if (vehicleData?.id) {
        onExistingVehicle?.(vehicleData);
      }
      const hasData = vehicleData?.manufacturer || vehicleData?.model || vehicleData?.vin;
      vehicleLookup.setVehicleLookedUp(!!hasData);
    },
    onLastCustomer: (customer) => onLastCustomer?.(customer),
    onTrialRestricted: () => setManualEntry(true),
  });

  const {
    lookupLoading,
    lookupError,
    vehicleLookedUp,
    showTrialInfo,
    showNonFinnishInfo,
    quotaInfo,
    extraLookupDialogOpen,
    setExtraLookupDialogOpen,
    vehicleTypeaheadOptions,
    vehicleTypeaheadLoading,
    vehicleTypeaheadOpenRef,
    handleRegistrationInput,
    selectTypeaheadOption,
    requestLookup,
    confirmExtraLookup,
    interruptLookup,
  } = vehicleLookup;

  const regNumber = vehicle?.registration_number || '';
  const showLookupButton = regNumber.length >= 3 && !vehicleLookedUp && !lookupLoading && !manualEntry;

  // Typing a new registration drops everything the previous one filled in
  const handleRegChange = (value) => {
    const clean = value.replace(/\s+/g, '').toUpperCase();
    onChange({ ...EMPTY_VEHICLE, registration_number: clean });
    setManualEntry(false);
    onExistingVehicle?.(null);
    handleRegistrationInput(clean);
  };

  const handleFieldChange = (name) => (event) => {
    onChange({ ...vehicle, [name]: event.target.value });
  };

  const handleLookupClick = () => {
    if (!regNumber) return;
    requestLookup(regNumber);
  };

  const enterManuallyButton = (
    <Button
      color="inherit"
      size="small"
      onClick={() => setManualEntry(true)}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {t('Enter manually')}
    </Button>
  );

  return (
    <Box data-testid={dataTestId}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
          {t('Registration Number')}{required ? ' *' : ''}
        </Typography>
        <HelpButton text={t('registrationNumberHelp')} size={16} />
      </Box>

      <Autocomplete
        freeSolo
        fullWidth
        disabled={disabled}
        open={vehicleTypeaheadOptions.length > 0 && !vehicleLookedUp}
        options={vehicleTypeaheadOptions}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.registration_number || '')}
        filterOptions={(x) => x}
        loading={vehicleTypeaheadLoading}
        onOpen={() => { vehicleTypeaheadOpenRef.current = true; }}
        onClose={() => { vehicleTypeaheadOpenRef.current = false; }}
        inputValue={regNumber}
        onInputChange={(event, newValue, reason) => {
          if (reason === 'input') {
            handleRegChange(newValue);
          } else if (reason === 'clear') {
            handleRegChange('');
          }
        }}
        onChange={(event, newValue, reason) => {
          if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
            selectTypeaheadOption(newValue);
          }
        }}
        renderOption={(props, option) => {
          const { key, ...restProps } = props;
          return (
            <Box component="li" key={option.id} {...restProps}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, ...plateFont }}>
                  {option.registration_number}
                  {(option.manufacturer || option.model) && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1, fontFamily: 'inherit', fontWeight: 400 }}
                    >
                      {[option.manufacturer, option.model].filter(Boolean).join(' ')}
                      {option.year_model ? ` (${option.year_model})` : ''}
                    </Typography>
                  )}
                </Typography>
                {option.last_customer_name && (
                  <Typography variant="caption" color="text.secondary">
                    {option.last_customer_name}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoComplete="off"
            autoFocus={autoFocus}
            required={required}
            size="small"
            placeholder="ABC-123"
            inputProps={{ ...params.inputProps, 'data-testid': `${dataTestId}-input` }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (showLookupButton && !vehicleTypeaheadOpenRef.current) {
                  handleLookupClick();
                }
              }
            }}
            InputProps={{
              ...params.InputProps,
              sx: { ...plateFont, fontSize: '14px' },
              endAdornment: (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {lookupLoading && (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Button
                          size="small"
                          onClick={() => { interruptLookup(); setManualEntry(true); }}
                          sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '12px', color: 'text.secondary' }}
                        >
                          {t('Enter manually')}
                        </Button>
                      </>
                    )}
                    {showLookupButton && (
                      <Button
                        size="small"
                        onClick={handleLookupClick}
                        startIcon={<SearchIcon sx={{ fontSize: '16px !important' }} />}
                        sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '12px', whiteSpace: 'nowrap' }}
                        data-testid={`${dataTestId}-lookup`}
                      >
                        {t('vehicleLookup.button')}
                      </Button>
                    )}
                    {vehicleLookedUp && !lookupLoading && (
                      <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                    )}
                  </Box>
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
        )}
      />

      {/* Quota indicator + the always-available way into manual entry */}
      {!vehicleLookedUp && !manualEntry && regNumber.length >= 3 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1, flexWrap: 'wrap' }}>
          {quotaInfo && quotaInfo.vehicle_data_provider === 'rekisterinumeroapi' ? (
            <>
              <Typography variant="caption" color="success.main">
                {t('vehicleLookup.fallbackProviderShort')}
              </Typography>
              <Typography variant="caption" color="text.secondary">|</Typography>
            </>
          ) : quotaInfo && (
            <>
              <Typography variant="caption" color="text.secondary">
                {t('vehicleLookup.quotaRemaining', {
                  remaining: Math.max(0, quotaInfo.remaining_lookups),
                  total: quotaInfo.included_lookups,
                })}
              </Typography>
              <HelpButton text={t('vehicleLookup.quotaHelp')} size={16} />
              <Typography variant="caption" color="text.secondary">|</Typography>
            </>
          )}
          <Typography
            variant="caption"
            color="secondary.main"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => setManualEntry(true)}
            data-testid={`${dataTestId}-manual-link`}
          >
            {t('vehicleLookup.enterManually')}
          </Typography>
        </Box>
      )}

      <Collapse in={!!lookupError && !manualEntry}>
        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '10px' }} action={enterManuallyButton}>
          {lookupError}
        </Alert>
      </Collapse>

      <Collapse in={showNonFinnishInfo && !manualEntry}>
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon />}
          sx={{ mt: 1.5, borderRadius: '10px' }}
          action={enterManuallyButton}
        >
          {t('Vehicle lookup is only available for Finnish registration numbers (e.g. ABC-123). You can enter vehicle details manually if needed.')}
        </Alert>
      </Collapse>

      <Collapse in={showTrialInfo && !manualEntry}>
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon />}
          sx={{ mt: 1.5, borderRadius: '10px' }}
          action={enterManuallyButton}
        >
          {t('vehicleLookup.trialMessage')}
        </Alert>
      </Collapse>

      {/* Found vehicle summary, with a pencil into manual editing */}
      <Collapse in={vehicleLookedUp && !!vehicle?.manufacturer && !manualEntry}>
        <Box
          sx={{
            mt: 1.5,
            p: 2,
            bgcolor: 'background.default',
            borderRadius: '10px',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DirectionsCarIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                {t('Vehicle Information')}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setManualEntry(true)}
              sx={{ color: 'text.secondary' }}
              data-testid={`${dataTestId}-edit`}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {[vehicle?.manufacturer, vehicle?.model].filter(Boolean).join(' ')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {vehicle?.year_model && <Chip size="small" label={`${vehicle.year_model}`} variant="outlined" />}
            {vehicle?.fuel && <Chip size="small" label={vehicle.fuel} variant="outlined" />}
            {vehicle?.power_kw && <Chip size="small" label={`${vehicle.power_kw} kW`} variant="outlined" />}
          </Box>
          {vehicle?.vin && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1, ...numFont, color: 'text.secondary' }}
            >
              VIN: {vehicle.vin}
            </Typography>
          )}
        </Box>
      </Collapse>

      {/* Manual entry: the fields a failed lookup used to hide, leaving nowhere to type */}
      <Collapse in={manualEntry}>
        <Box
          sx={{
            mt: 1.5,
            p: 2,
            bgcolor: 'background.default',
            borderRadius: '10px',
            border: '1px solid',
            borderColor: 'divider',
          }}
          data-testid={`${dataTestId}-manual`}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DirectionsCarIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                {t('Vehicle Information')}
              </Typography>
            </Box>
            {vehicleLookedUp && (
              <Button size="small" onClick={() => setManualEntry(false)} sx={{ color: 'text.secondary' }}>
                {t('Cancel')}
              </Button>
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('Manufacturer')}
                placeholder={t('e.g. Toyota')}
                value={vehicle?.manufacturer || ''}
                onChange={handleFieldChange('manufacturer')}
                fullWidth
                size="small"
                inputProps={{ 'data-testid': `${dataTestId}-manufacturer` }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('Model')}
                placeholder={t('e.g. Corolla')}
                value={vehicle?.model || ''}
                onChange={handleFieldChange('model')}
                fullWidth
                size="small"
                inputProps={{ 'data-testid': `${dataTestId}-model` }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label={t('Year')}
                placeholder={t('e.g. 2020')}
                value={vehicle?.year_model || ''}
                onChange={handleFieldChange('year_model')}
                fullWidth
                size="small"
                type="number"
                inputProps={{ 'data-testid': `${dataTestId}-year` }}
              />
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField
                label="VIN"
                value={vehicle?.vin || ''}
                onChange={handleFieldChange('vin')}
                fullWidth
                size="small"
                inputProps={{ style: { textTransform: 'uppercase' }, 'data-testid': `${dataTestId}-vin` }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      <ExtraLookupConfirmDialog
        open={extraLookupDialogOpen}
        onClose={() => setExtraLookupDialogOpen(false)}
        onConfirm={() => confirmExtraLookup()}
        quotaInfo={quotaInfo}
      />
    </Box>
  );
}

export default VehicleRegistrationField;
